// Recommendation Engine - Generates personalized recommendations

/**
 * Generates personalized recommendations based on analysis
 * @param {Object} analysisData - Complete analysis data
 * @returns {Array} Array of recommendation objects
 */
export function generateRecommendations(analysisData) {
  const recommendations = [];

  // Cognitive recommendations
  recommendations.push(...generateCognitiveRecommendations(analysisData.cognitiveProfile));

  // Behavioral recommendations
  recommendations.push(...generateBehavioralRecommendations(analysisData.behavioralProfile));

  // Test performance recommendations
  recommendations.push(...generateTestRecommendations(analysisData.testPerformance));

  // Assignment recommendations
  recommendations.push(...generateAssignmentRecommendations(analysisData.assignmentAnalysis));

  // Attendance recommendations
  recommendations.push(...generateAttendanceRecommendations(analysisData.attendanceAnalysis));

  // Doubt recommendations
  recommendations.push(...generateDoubtRecommendations(analysisData.doubtAnalysis));

  // Revision recommendations
  recommendations.push(...generateRevisionRecommendations(analysisData.revisionAnalysis));

  // Prioritize and categorize
  const prioritized = prioritizeRecommendations(recommendations);
  const categorized = categorizeRecommendations(prioritized);

  return categorized;
}

/**
 * Generates cognitive-based recommendations
 */
function generateCognitiveRecommendations(cognitiveProfile) {
  const recommendations = [];

  if (!cognitiveProfile) return recommendations;

  // Learning style recommendations
  if (cognitiveProfile.learningStyle === 'visual') {
    recommendations.push({
      id: 'cog-visual-1',
      type: 'cognitive',
      priority: 'medium',
      title: 'Leverage Your Visual Learning Style',
      description: 'You learn best through visual aids. Use diagrams, mind maps, and flowcharts.',
      actionItems: [
        'Create visual notes for complex topics',
        'Watch video lectures and animations',
        'Use color coding in your study materials'
      ],
      expectedImpact: 'Improved retention and understanding',
      estimatedTimeToImplement: '1-2 weeks'
    });
  } else if (cognitiveProfile.learningStyle === 'analytical') {
    recommendations.push({
      id: 'cog-analytical-1',
      type: 'cognitive',
      priority: 'medium',
      title: 'Optimize Your Analytical Approach',
      description: 'You excel at logical reasoning. Focus on problem-solving and step-by-step analysis.',
      actionItems: [
        'Practice more calculation-based problems',
        'Break down complex problems into steps',
        'Focus on understanding underlying principles'
      ],
      expectedImpact: 'Enhanced problem-solving speed',
      estimatedTimeToImplement: '2-3 weeks'
    });
  }

  // Time management recommendations
  if (cognitiveProfile.timeManagement) {
    if (cognitiveProfile.timeManagement.rushingTendency > 50) {
      recommendations.push({
        id: 'cog-time-1',
        type: 'cognitive',
        priority: 'high',
        title: 'Reduce Rushing Tendency',
        description: 'You tend to rush through questions. Slow down and read carefully.',
        actionItems: [
          'Practice timed tests with focus on accuracy',
          'Read each question twice before answering',
          'Allocate specific time per question'
        ],
        expectedImpact: 'Improved accuracy by 10-15%',
        estimatedTimeToImplement: '3-4 weeks'
      });
    }

    if (cognitiveProfile.timeManagement.overthinkingTendency > 50) {
      recommendations.push({
        id: 'cog-time-2',
        type: 'cognitive',
        priority: 'high',
        title: 'Overcome Overthinking',
        description: 'You spend too much time on difficult questions. Learn to move on strategically.',
        actionItems: [
          'Set a 3-minute limit per question',
          'Mark difficult questions and return later',
          'Practice quick decision-making'
        ],
        expectedImpact: 'Complete more questions in tests',
        estimatedTimeToImplement: '2-3 weeks'
      });
    }
  }

  // Weakness-based recommendations
  if (cognitiveProfile.weaknesses && cognitiveProfile.weaknesses.length > 0) {
    const topWeakness = cognitiveProfile.weaknesses[0];
    recommendations.push({
      id: 'cog-weak-1',
      type: 'cognitive',
      priority: 'high',
      title: `Focus on ${topWeakness.topic}`,
      description: `Your performance in ${topWeakness.topic} needs improvement (${topWeakness.score}% accuracy).`,
      actionItems: [
        `Complete 20 practice questions on ${topWeakness.topic}`,
        `Watch tutorial videos on ${topWeakness.topic}`,
        `Seek help from teachers or peers`
      ],
      expectedImpact: 'Improve topic accuracy by 20-30%',
      estimatedTimeToImplement: '2-4 weeks'
    });
  }

  return recommendations;
}

/**
 * Generates behavioral recommendations
 */
function generateBehavioralRecommendations(behavioralProfile) {
  const recommendations = [];

  if (!behavioralProfile) return recommendations;

  // Study consistency
  if (behavioralProfile.studyConsistency && behavioralProfile.studyConsistency.score < 60) {
    recommendations.push({
      id: 'beh-consistency-1',
      type: 'behavioral',
      priority: 'high',
      title: 'Build Study Consistency',
      description: 'Your study pattern is irregular. Establish a consistent routine.',
      actionItems: [
        'Set fixed study hours daily',
        'Create a weekly study schedule',
        'Track your study streak'
      ],
      expectedImpact: 'Better retention and reduced stress',
      estimatedTimeToImplement: '2-3 weeks'
    });
  }

  // Procrastination
  if (behavioralProfile.procrastinationIndex > 50) {
    recommendations.push({
      id: 'beh-procrastination-1',
      type: 'behavioral',
      priority: 'high',
      title: 'Overcome Procrastination',
      description: 'You tend to delay tasks. Start assignments and revision earlier.',
      actionItems: [
        'Break large tasks into smaller chunks',
        'Use the 2-minute rule for quick tasks',
        'Set early personal deadlines'
      ],
      expectedImpact: 'Reduced stress and better quality work',
      estimatedTimeToImplement: '3-4 weeks'
    });
  }

  // Engagement
  if (behavioralProfile.engagementLevel && behavioralProfile.engagementLevel.overall < 50) {
    recommendations.push({
      id: 'beh-engagement-1',
      type: 'behavioral',
      priority: 'medium',
      title: 'Increase Class Engagement',
      description: 'Your engagement level is low. Participate more actively in classes.',
      actionItems: [
        'Ask at least one question per class',
        'Participate in discussions',
        'Complete all assignments on time'
      ],
      expectedImpact: 'Better understanding and teacher rapport',
      estimatedTimeToImplement: '1-2 weeks'
    });
  }

  // Attendance
  if (behavioralProfile.attendanceRate < 75) {
    recommendations.push({
      id: 'beh-attendance-1',
      type: 'behavioral',
      priority: 'high',
      title: 'Improve Attendance',
      description: `Your attendance is ${behavioralProfile.attendanceRate}%. Aim for at least 85%.`,
      actionItems: [
        'Set reminders for classes',
        'Prioritize attendance over other activities',
        'Catch up on missed classes immediately'
      ],
      expectedImpact: 'Better learning outcomes',
      estimatedTimeToImplement: 'Ongoing'
    });
  }

  return recommendations;
}

/**
 * Generates test performance recommendations
 */
function generateTestRecommendations(testPerformance) {
  const recommendations = [];

  if (!testPerformance || testPerformance.totalTests === 0) {
    recommendations.push({
      id: 'test-start-1',
      type: 'study_strategy',
      priority: 'high',
      title: 'Start Taking Practice Tests',
      description: 'You haven\'t taken many tests. Regular practice is essential.',
      actionItems: [
        'Take at least 2 mock tests per week',
        'Review your mistakes after each test',
        'Track your progress over time'
      ],
      expectedImpact: 'Build exam confidence and identify gaps',
      estimatedTimeToImplement: 'Ongoing'
    });
    return recommendations;
  }

  // Low average score
  if (testPerformance.averageScore < 60) {
    recommendations.push({
      id: 'test-score-1',
      type: 'study_strategy',
      priority: 'high',
      title: 'Improve Test Scores',
      description: `Your average score is ${testPerformance.averageScore}%. Focus on fundamentals.`,
      actionItems: [
        'Review basic concepts thoroughly',
        'Practice easier questions first',
        'Gradually increase difficulty level'
      ],
      expectedImpact: 'Raise average score by 15-20%',
      estimatedTimeToImplement: '4-6 weeks'
    });
  }

  // Sectional weaknesses
  if (testPerformance.sectionalPerformance) {
    const weakSections = testPerformance.sectionalPerformance
      .filter(s => s.averageScore < 50)
      .sort((a, b) => a.averageScore - b.averageScore);

    if (weakSections.length > 0) {
      const weakest = weakSections[0];
      recommendations.push({
        id: 'test-section-1',
        type: 'study_strategy',
        priority: 'high',
        title: `Strengthen ${weakest.section}`,
        description: `Your ${weakest.section} section needs attention (${weakest.averageScore}% average).`,
        actionItems: [
          `Dedicate 30 minutes daily to ${weakest.section}`,
          `Complete section-specific practice tests`,
          `Identify and fix conceptual gaps`
        ],
        expectedImpact: 'Balanced sectional performance',
        estimatedTimeToImplement: '3-4 weeks'
      });
    }
  }

  return recommendations;
}

/**
 * Generates assignment recommendations
 */
function generateAssignmentRecommendations(assignmentAnalysis) {
  const recommendations = [];

  if (!assignmentAnalysis) return recommendations;

  if (assignmentAnalysis.submissionPattern === 'late') {
    recommendations.push({
      id: 'assign-late-1',
      type: 'behavioral',
      priority: 'high',
      title: 'Submit Assignments On Time',
      description: 'You frequently submit assignments late. Improve time management.',
      actionItems: [
        'Start assignments as soon as they\'re assigned',
        'Set reminders 2 days before deadline',
        'Break assignments into daily tasks'
      ],
      expectedImpact: 'Better grades and reduced stress',
      estimatedTimeToImplement: '2-3 weeks'
    });
  }

  return recommendations;
}

/**
 * Generates attendance recommendations
 */
function generateAttendanceRecommendations(attendanceAnalysis) {
  const recommendations = [];

  if (!attendanceAnalysis) return recommendations;

  if (attendanceAnalysis.overallAttendance < 75) {
    recommendations.push({
      id: 'attend-low-1',
      type: 'behavioral',
      priority: 'high',
      title: 'Boost Your Attendance',
      description: `Your attendance is ${attendanceAnalysis.overallAttendance}%. Regular attendance is crucial.`,
      actionItems: [
        'Attend all scheduled classes',
        'Inform teachers in advance if absent',
        'Review missed class materials immediately'
      ],
      expectedImpact: 'Better understanding and performance',
      estimatedTimeToImplement: 'Ongoing'
    });
  }

  return recommendations;
}

/**
 * Generates doubt-related recommendations
 */
function generateDoubtRecommendations(doubtAnalysis) {
  const recommendations = [];

  if (!doubtAnalysis) return recommendations;

  if (doubtAnalysis.doubtFrequency === 'low') {
    recommendations.push({
      id: 'doubt-low-1',
      type: 'behavioral',
      priority: 'medium',
      title: 'Ask More Questions',
      description: 'You rarely ask doubts. Don\'t hesitate to seek clarification.',
      actionItems: [
        'Identify unclear concepts after each class',
        'Post at least 2 doubts per week',
        'Participate in doubt resolution sessions'
      ],
      expectedImpact: 'Clearer understanding of concepts',
      estimatedTimeToImplement: '1-2 weeks'
    });
  }

  if (doubtAnalysis.topConfusionAreas && doubtAnalysis.topConfusionAreas.length > 0) {
    const topArea = doubtAnalysis.topConfusionAreas[0];
    recommendations.push({
      id: 'doubt-confusion-1',
      type: 'study_strategy',
      priority: 'medium',
      title: `Clear Confusion in ${topArea.topic}`,
      description: `You have ${topArea.doubtCount} doubts in ${topArea.topic}. Focus on this area.`,
      actionItems: [
        `Review ${topArea.topic} fundamentals`,
        `Seek one-on-one help for ${topArea.topic}`,
        `Practice more problems in this area`
      ],
      expectedImpact: 'Reduced confusion and better clarity',
      estimatedTimeToImplement: '2-3 weeks'
    });
  }

  return recommendations;
}

/**
 * Generates revision recommendations
 */
function generateRevisionRecommendations(revisionAnalysis) {
  const recommendations = [];

  if (!revisionAnalysis) return recommendations;

  if (revisionAnalysis.totalPracticeSessions < 10) {
    recommendations.push({
      id: 'revision-low-1',
      type: 'study_strategy',
      priority: 'high',
      title: 'Increase Revision Frequency',
      description: 'You need more regular revision. Practice makes perfect.',
      actionItems: [
        'Schedule daily 30-minute revision sessions',
        'Review wrong questions weekly',
        'Create a revision timetable'
      ],
      expectedImpact: 'Better retention and test performance',
      estimatedTimeToImplement: '2-3 weeks'
    });
  }

  if (revisionAnalysis.revisionEffectiveness < 60) {
    recommendations.push({
      id: 'revision-effectiveness-1',
      type: 'study_strategy',
      priority: 'medium',
      title: 'Improve Revision Quality',
      description: 'Your revision isn\'t translating to better performance. Change your approach.',
      actionItems: [
        'Focus on understanding, not memorization',
        'Test yourself after each revision session',
        'Use active recall techniques'
      ],
      expectedImpact: 'More effective learning',
      estimatedTimeToImplement: '3-4 weeks'
    });
  }

  return recommendations;
}

/**
 * Prioritizes recommendations by impact and urgency
 */
export function prioritizeRecommendations(recommendations) {
  const priorityOrder = { high: 3, medium: 2, low: 1 };
  
  return recommendations.sort((a, b) => {
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
}

/**
 * Categorizes recommendations
 */
export function categorizeRecommendations(recommendations) {
  const categorized = {
    cognitive: [],
    behavioral: [],
    study_strategy: []
  };

  recommendations.forEach(rec => {
    if (categorized[rec.type]) {
      categorized[rec.type].push(rec);
    }
  });

  // Return as flat array with category markers
  return recommendations;
}
